import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Flame, Droplets, Wind, Mountain, Sparkles } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const elementIcons = { fire: Flame, water: Droplets, wind: Wind, earth: Mountain };
const elementPieColors = { fire: "#ef4444", water: "#06b6d4", wind: "#10b981", earth: "#d97706" };
const elementDescriptions = {
  fire: "Drive & Direction",
  water: "Care & Connection",
  wind: "Clarity & Adaptability",
  earth: "Stability & Structure"
};
const tooltipContentStyle = { 
  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
  border: '1px solid #e5e7eb',
  borderRadius: '8px'
};

const StageColorBar = ({ stageElements }) => (
  <div className="flex gap-0.5 h-3 rounded-sm overflow-hidden" data-testid="stage-color-bar">
    {stageElements.map((element, idx) => (
      <div
        key={`stage-${idx}-${element}`}
        className="h-full flex-1"
        style={{ backgroundColor: elementPieColors[element] || '#9ca3af', minWidth: '12px' }}
      />
    ))}
  </div>
);

export const ArchetypeCard = ({ archetype, profile, essence_sentence, external_perception, closing_style, expandedSections, setExpandedSections }) => (
  <Card className="shadow-2xl border-0 overflow-hidden" style={{ backgroundColor: '#231F20' }}>
    <CardContent className="p-4 sm:p-6 md:p-8 lg:p-12">
      <div className="space-y-6">
        <div className="text-center space-y-3 px-2">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase tracking-wide break-words">
            {archetype?.name || `The ${profile.dominant.charAt(0).toUpperCase() + profile.dominant.slice(1)}-${profile.secondary.charAt(0).toUpperCase() + profile.secondary.slice(1)} Leader`}
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 italic max-w-3xl mx-auto mt-4 px-2">
            {archetype?.tagline || essence_sentence}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mt-6 sm:mt-8">
          <div>
            <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-3 sm:mb-4 uppercase tracking-wide">Strengths</h3>
            <ul className="space-y-2">
              {archetype?.strengths?.map((s, i) => (
                <li key={`strength-${i}`} className="text-sm sm:text-base text-gray-300 flex items-start gap-2">
                  <span className="text-[#FFBD25] mt-1 flex-shrink-0">▸</span>
                  <span className="break-words">{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-3 sm:mb-4 uppercase tracking-wide">Watchpoints</h3>
            <ul className="space-y-2">
              {archetype?.watchpoints?.map((w, i) => (
                <li key={`watchpoint-${i}`} className="text-sm sm:text-base text-gray-300 flex items-start gap-2">
                  <span className="text-[#FF751F] mt-1 flex-shrink-0">▸</span>
                  <span className="break-words">{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {external_perception && (
          <div className="mt-8 pt-6 border-t border-gray-700">
            <h3 className="text-sm sm:text-base md:text-lg font-bold text-white mb-3 uppercase tracking-wide">External Perception</h3>
            <Accordion type="multiple" value={expandedSections} onValueChange={setExpandedSections} className="w-full">
              <AccordionItem value="external-perception" className="border-0">
                <AccordionTrigger className="text-left hover:no-underline py-2 [&>svg]:text-gray-400 [&>svg]:hover:text-white">
                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed break-words px-2 pr-4">
                    {typeof external_perception === 'object' ? external_perception.condensed : external_perception}
                  </p>
                </AccordionTrigger>
                {typeof external_perception === 'object' && external_perception.expanded && (
                  <AccordionContent>
                    <div className="pt-2 pb-4 px-2">
                      <p className="text-sm sm:text-base text-gray-300 leading-relaxed whitespace-pre-line break-words">
                        {external_perception.expanded}
                      </p>
                    </div>
                  </AccordionContent>
                )}
              </AccordionItem>
            </Accordion>
          </div>
        )}

        {closing_style && (
          <div className="mt-8 pt-6 border-t border-gray-700">
            <h3 className="text-sm sm:text-base md:text-lg font-bold text-white mb-3 uppercase tracking-wide">Your Closing Style</h3>
            <Accordion type="multiple" value={expandedSections} onValueChange={setExpandedSections} className="w-full">
              <AccordionItem value="closing-style" className="border-0">
                <AccordionTrigger className="text-left hover:no-underline py-2 [&>svg]:text-gray-400 [&>svg]:hover:text-white">
                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed break-words px-2 pr-4">
                    {typeof closing_style === 'object' ? closing_style.condensed : closing_style}
                  </p>
                </AccordionTrigger>
                {typeof closing_style === 'object' && closing_style.expanded && (
                  <AccordionContent>
                    <div className="pt-2 pb-4 px-2">
                      <p className="text-sm sm:text-base text-gray-300 leading-relaxed whitespace-pre-line break-words">
                        {closing_style.expanded}
                      </p>
                    </div>
                  </AccordionContent>
                )}
              </AccordionItem>
            </Accordion>
          </div>
        )}

        {archetype?.mantra && (
          <div className="mt-8 pt-6 border-t border-gray-700">
            <h3 className="text-sm sm:text-base md:text-lg font-bold text-white mb-3 uppercase tracking-wide">Your Mantra</h3>
            <p className="text-base sm:text-lg md:text-xl text-[#FFD04A] italic text-center break-words px-2">
              &ldquo;{archetype.mantra}&rdquo;
            </p>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

export const ElementDistribution = ({ profile, sortedElements }) => (
  <Card className="shadow-xl border-0 backdrop-blur-sm bg-white/95">
    <CardHeader className="p-4 sm:p-6">
      <CardTitle className="flex items-center gap-2 text-lg sm:text-xl md:text-2xl">
        <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" style={{ color: '#FF751F' }} />
        <span className="break-words">Your Elemental Distribution</span>
      </CardTitle>
      <CardDescription className="text-sm sm:text-base">
        How your 100 assessment points are distributed across the four elements
      </CardDescription>
    </CardHeader>
    <CardContent className="p-4 sm:p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
        <div className="h-64 sm:h-80 w-full" style={{ minWidth: 200, minHeight: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={Object.entries(profile.scores).map(([element, score]) => ({
                  name: element.charAt(0).toUpperCase() + element.slice(1),
                  value: score,
                  element
                }))}
                cx="50%" cy="50%" labelLine={false}
                label={({ name }) => `${name}`}
                outerRadius={100} fill="#8884d8" dataKey="value"
              >
                {Object.entries(profile.scores).map(([element]) => (
                  <Cell key={`cell-${element}`} fill={elementPieColors[element]} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [name, '']} contentStyle={tooltipContentStyle} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-3">
          {sortedElements.map(([element]) => {
            const Icon = elementIcons[element];
            const isTop = element === profile.dominant || element === profile.secondary;
            return (
              <div key={element} data-testid={`element-${element}`}
                className={`p-4 rounded-xl border-2 ${isTop ? "bg-orange-50" : "border-gray-200 bg-white"}`}
                style={isTop ? { borderColor: '#FF751F' } : {}}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: elementPieColors[element] }}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold capitalize text-base" style={{ color: '#231F20' }}>{element}</h3>
                    <p className="text-xs text-gray-600">{elementDescriptions[element]}</p>
                  </div>
                </div>
                {element === profile.dominant && <p className="text-xs font-semibold mt-2" style={{ color: '#FF751F' }}>■ Dominant Element</p>}
                {element === profile.secondary && <p className="text-xs font-semibold mt-2" style={{ color: '#FFBD25' }}>■ Secondary Element</p>}
              </div>
            );
          })}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const LeadershipPattern = ({ profile, archetype_overview, expandedSections, setExpandedSections }) => (
  <Card className="shadow-xl border-0 backdrop-blur-sm bg-white/95">
    <CardHeader>
      <CardTitle>Your Leadership Pattern</CardTitle>
      <CardDescription>How your elements combine to shape your executive presence</CardDescription>
    </CardHeader>
    <CardContent>
      <Accordion type="multiple" value={expandedSections} onValueChange={setExpandedSections} className="w-full">
        <AccordionItem value="leadership-pattern" className="border-0">
          <AccordionTrigger className="text-left hover:no-underline py-3 sm:py-4">
            <div className="text-sm sm:text-base text-gray-800 leading-relaxed pr-4 break-words">
              You lead through a combination of {profile.dominant.charAt(0).toUpperCase() + profile.dominant.slice(1)} and {profile.secondary.charAt(0).toUpperCase() + profile.secondary.slice(1)} energies, creating a leadership style that feels both instinctive and reliable.
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-2 pb-4 px-1">
              <p className="text-sm sm:text-base text-gray-800 leading-relaxed whitespace-pre-line break-words">{archetype_overview}</p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </CardContent>
  </Card>
);

export const ScenarioInsights = ({ scenario_insights, expandedSections, setExpandedSections }) => (
  <Card className="shadow-xl border-0 backdrop-blur-sm bg-white/95">
    <CardHeader>
      <CardTitle>Scenario Insights</CardTitle>
      <CardDescription>Your response patterns across five executive challenges</CardDescription>
    </CardHeader>
    <CardContent>
      <Accordion type="multiple" value={expandedSections} onValueChange={setExpandedSections} className="w-full">
        {scenario_insights.map((insight) => (
          <AccordionItem key={insight.scenario} value={`scenario-${insight.scenario}`} data-testid={`scenario-insight-${insight.scenario}`} className="border-b border-gray-200 last:border-0">
            <AccordionTrigger className="text-left hover:no-underline py-3 sm:py-4">
              <div className="flex flex-col items-start gap-2 pr-4 break-words">
                <div className="flex items-center gap-3">
                  <span className="text-sm sm:text-base font-bold uppercase tracking-wide break-words" style={{ color: '#231F20' }}>{insight.scenario}</span>
                  <StageColorBar stageElements={insight.stage_elements} />
                </div>
                <span className="text-xs sm:text-sm text-gray-700 font-normal leading-relaxed break-words">{insight.quick_insight}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="pt-2 pb-4 pl-0 px-1">
                <p className="text-sm sm:text-base text-gray-800 leading-relaxed break-words">{insight.detailed_insight}</p>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </CardContent>
  </Card>
);

export const GrowthPath = ({ growth_path, expandedSections, setExpandedSections }) => (
  <Card className="shadow-xl border-0 backdrop-blur-sm bg-gradient-to-br from-green-50 to-emerald-50">
    <CardHeader>
      <CardTitle>Your Growth Path</CardTitle>
      <CardDescription>Expanding your leadership repertoire through less-utilized elements</CardDescription>
    </CardHeader>
    <CardContent>
      <Accordion type="multiple" value={expandedSections} onValueChange={setExpandedSections} className="w-full">
        <AccordionItem value="growth-path" className="border-0">
          <AccordionTrigger className="text-left hover:no-underline py-3 sm:py-4 bg-white/70 px-3 sm:px-4 rounded-lg">
            <div className="text-sm sm:text-base text-gray-800 leading-relaxed pr-4 break-words">{growth_path.summary}</div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-4 pb-2 px-3 sm:px-4 bg-white/70 rounded-lg mt-2">
              <p className="text-sm sm:text-base text-gray-800 leading-relaxed whitespace-pre-line break-words">{growth_path.detail}</p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </CardContent>
  </Card>
);
